import {SavedTeamSettings, TeamSettingsRequest} from '../payroll-workforce/workforce-team-settings.state';
import {TeamCreationIntent, TeamCreationProgress} from '../payroll-workforce/workforce-team-creation.state';
import { Injectable } from '@angular/core';
import { Restangular } from 'ngx-restangular';
import {AppSettings} from '../../app.settings';
import {WorkforceContext, WorkforcePage, WorkforceOptions} from '../payroll-workforce/workforce-browse.state';

export const UKG_READY_PROVIDER_SLUG = 'ukg_ready';
export const PAYCOR_PROVIDER_SLUG = 'paycor';

export interface PayrollProviderCapabilities {
  supportsAutoAddFromSavedRules?: boolean;
  supportsCensus?: boolean;
  supportsCensusFilters?: boolean;
  supportsCensusMetadata?: boolean;
  supportsDeductionCodes?: boolean;
  supportsDeductionEnrollmentCancellation?: boolean;
  supportsDeductionEnrollmentStatus?: boolean;
  supportsDeductionImportHistory?: boolean;
  supportsDeductionImportReplay?: boolean;
  supportsDeductionImportStatusRefresh?: boolean;
  supportsDirectDeductionEnrollment?: boolean;
  supportsEmployeeLookupForPurchase?: boolean;
  supportsFilteredTeams?: boolean;
  supportsInternalTeamCreationFromCensus?: boolean;
  supportsPayrollDeductionSetup?: boolean;
  supportsScheduledDeductionImport?: boolean;
  supportsImportStatusRefresh?: boolean;
  [key: string]: boolean | undefined;
}

export interface PayrollProviderCatalogItem {
  aliases?: string[];
  capabilities?: PayrollProviderCapabilities;
  connectionLevel?: string;
  defaultSettings?: Record<string, any>;
  metadata?: Record<string, any>;
  name?: string;
  slug?: string;
  status?: string;
}

export interface PayrollProviderConnectionStatusRow {
  activeConnections: number;
  activeCredentialCompanies: number;
  availableSettings: number;
  connectionStatusCounts: Record<string, number>;
  credentialRecords: number;
  inactiveConnections: number;
  latestHealthState?: string;
  latestVerifiedAt?: string | null;
  name: string;
  providerCatalogStatus?: string;
  providerSlug: string;
  settingStatusCounts: Record<string, number>;
  status: 'active' | 'inactive' | 'needs_credentials' | 'not_configured' | 'partially_unavailable';
  totalConnections: number;
  unavailableSettings: number;
}

export interface PayrollProviderConnectionStatusSummary {
  generatedAt: string;
  legacyCompatibility?: Record<string, any>;
  providerSlug: string;
  providers: PayrollProviderConnectionStatusRow[];
  totals: {
    activeProviderConnections: number;
    companiesWithActiveProviderCredentials: number;
    configuredProviders: number;
    providerConnections: number;
    providerCredentialRecords: number;
  };
}

export interface PayrollProviderConnectionResponse {
  capabilities?: PayrollProviderCapabilities;
  companyId?: string;
  connection?: {
    credential?: any;
    displayName?: string;
    externalConnectionId?: string;
    id?: string;
    isActive?: boolean;
    lastVerifiedAt?: string | null;
    metadata?: Record<string, any>;
    providerSlug?: string;
    settings?: Record<string, any>;
    status?: string;
  };
  isCompatibilityFallback?: boolean;
  provider?: PayrollProviderCatalogItem;
  providerSlug?: string;
  source?: string;
}

export interface PayrollCensusCompletenessWarning {
  code?: string;
  message?: string;
  missingEmailCount?: number;
  [key: string]: any;
}

export interface PayrollCensusCompleteness {
  complete: boolean;
  employeeCollectionComplete: boolean;
  emailComplete: boolean;
  missingEmailCount: number;
  warnings: PayrollCensusCompletenessWarning[];
}

export interface PayrollCensusResponse {
  completeness: PayrollCensusCompleteness;
  employees: any[];
  filterOptions?: Record<string, string[]>;
  snapshotBuiltAt?: string;
}

export interface PayrollCensusRequestOptions {
  includeFilterOptions?: boolean;
  refresh?: boolean;
}

export interface PayrollCensusAttributeCriterion {
  field: string;
  label?: string;
  operator: string;
  value: string | string[];
  displayValue?: string;
}

export interface PayrollCensusAttributeSnapshotCriteria {
  activeOnly?: boolean;
  criteria: PayrollCensusAttributeCriterion[];
  eligibleStatuses?: string[];
  match: 'all' | 'any';
  missingEmailPolicy?: string;
  mode: 'snapshot';
  providerSlug?: string;
}

export interface UkgReadyConnectionSetupInput {
  baseUrl?: string;
  clientId: string;
  clientSecret?: string;
  companyShortName: string;
}

export interface UkgReadyConnectionSetupResponse {
  company?: any;
  connection?: PayrollProviderConnectionResponse | null;
  legacyCompatibility?: {
    fieldsWritten?: string[];
    payrollConfigPath?: string;
    writesNonSecretPayrollConfig?: boolean;
  };
  providerSlug?: string;
}

export interface PaycorConnectionSetupInput {
  clientId: string;
  clientSecret?: string;
  environment?: 'sandbox';
  legalEntityId?: string;
  legalEntityName?: string;
  refreshToken?: string;
  subscriptionKey?: string;
  tenantId?: string;
}

export interface PaycorLegalEntityMetadata {
  legalEntityId?: string;
  legalEntityName?: string;
  tenantId?: string;
}

export interface PaycorLegalEntityDiscoveryResponse {
  activatedLegalEntityCount?: number;
  legalEntities?: PaycorLegalEntityMetadata[];
  providerSlug?: string;
}

export interface PaycorConnectionSetupResponse {
  connection?: PayrollProviderConnectionResponse | null;
  providerSlug?: string;
  savedConnection?: any;
  validation?: {
    activatedLegalEntityCount?: number;
    legalEntityDetailsRead?: boolean;
    selectedLegalEntity?: {
      legalEntityId?: string;
      legalEntityName?: string;
      tenantId?: string;
    } | null;
  };
}

export interface UkgConnectionCachedHealth {
  state?: string;
  host?: string;
  environmentLabel?: string;
  lastCheckedAt?: string | null;
  lastSuccessAt?: string | null;
  lastFailureAt?: string | null;
  currentIncidentStartedAt?: string | null;
  lastRecoveredAt?: string | null;
  lastErrorStatus?: number | null;
  lastErrorCode?: string | null;
  lastErrorMessage?: string | null;
  consecutiveFailures?: number;
}

export interface UkgStatusSummary {
  overallState: 'healthy' | 'degraded' | 'outage' | 'credentials_issue' | 'unknown' | 'not_configured';
  host: string;
  environmentLabel: string;
  totalConfiguredCompanies: number;
  impactedCompanyCount: number;
  currentIncidentStartedAt: string | null;
  lastSuccessAt: string | null;
  lastFailureAt: string | null;
  escalationThresholdHours: number;
  shouldContactVendor: boolean;
}

export interface UkgStatusRefreshResult {
  scope: 'impacted' | 'all';
  companiesChecked: number;
  companiesSucceeded: number;
  companiesFailed: number;
  refreshedAt: string;
  summary: UkgStatusSummary;
}

export type PayrollConnectionHealth = 'none' | 'healthy' | 'error';

export interface PayrollProviderConnectionHealthCheck {
  checks?: Record<string, {
    checkedAt?: string;
    recordCount?: number;
    state?: string;
  }>;
  checkedAt?: string;
  companyId?: string;
  dataFreshness?: {
    employeeDataReturned?: boolean;
    lastReadAt?: string;
    readTypes?: string[];
    source?: string;
  };
  lastSuccessfulReadAt?: string;
  providerSlug?: string;
  readTypes?: string[];
  state?: 'healthy' | 'error' | string;
}

export interface UkgDeductionCode {
  code: string;
  description: string;
  name?: string;
  preTax: boolean;
  id?: string | number;
  status?: string;
}

export interface PayrollDeductionSetupCode {
  code: string;
  description: string;
  deductionCodeId?: string | number | null;
  preTax: boolean;
  isAllowed: boolean;
  isCurrentProviderCode?: boolean;
  isCurrentUkgCode: boolean;
  mappedProductIds: string[];
}

export interface PayrollDeductionProductMapping {
  deductionCode?: string;
  deductionCodeDescription?: string;
  deductionCodeId?: string | number | null;
  deductionRules?: PayrollDeductionRule[];
}

export interface PayrollDeductionRule {
  planId?: string;
  planLabel?: string;
  tier?: string;
  deductionCode: string;
  deductionCodeDescription?: string;
  deductionCodeId?: string | number | null;
}

export interface PayrollDeductionSetup {
  companyId: string;
  providerConfigured?: boolean;
  providerName?: string;
  providerSlug?: string;
  providerSyncError?: string;
  ukgConfigured: boolean;
  ukgSyncError?: string;
  availableDeductionCodes: PayrollDeductionSetupCode[];
  allowedDeductionCodes: PayrollDeductionSetupCode[];
  payrollDeductionDisabledProducts?: string[];
  productPayrollDeductionMap: Record<string, PayrollDeductionProductMapping>;
}

export interface PayrollDeductionSetupQueryOptions {
  includeLiveDeductionCodes?: boolean;
}

export interface PayrollDeductionImportRecord {
  id: string;
  companyId?: string;
  teamId?: string;
  userId?: string;
  vendorProductId?: string;
  stripeCustomerId?: string;
  stripeSubscriptionId?: string;
  stripeInvoiceId?: string;
  action?: string;
  source?: string;
  status?: string;
  ukgImportJobId?: string;
  importTypeId?: string;
  deductionCode?: string;
  deductionName?: string;
  annualAmountCents?: number | null;
  perPayAmountCents?: number | null;
  numPayPeriodsPerYear?: number | null;
  effectiveFrom?: string;
  effectiveTo?: string;
  planId?: string;
  planLabel?: string;
  tier?: string;
  csvSha256?: string;
  redactedCsv?: string;
  redactedRowJson?: Record<string, any>;
  rawResponseJson?: any;
  rawStatusJson?: any;
  errorJson?: any;
  submittedAt?: string | null;
  statusCheckedAt?: string | null;
  completedAt?: string | null;
  replayOfImportId?: string;
  replayedByUserId?: string;
  replayReason?: string;
  metadata?: Record<string, any>;
  createdAt?: string;
  modifiedAt?: string;
  email?: string;
  firstName?: string;
  lastName?: string;
  vendorName?: string;
  productName?: string;
}

export interface PayrollDeductionImportListResponse {
  items: PayrollDeductionImportRecord[];
  limit: number;
  page: number;
  total: number;
}

export interface PayrollDeductionImportRefreshResponse {
  failed: number;
  page: number;
  refreshed: number;
  results: Array<{
    id?: string;
    reason?: string;
    status?: string;
    ukgImportJobId?: string;
  }>;
  skipped: number;
  total: number;
}

export interface PayrollDeductionImportListQuery {
  action?: string;
  filter?: string;
  limit?: number;
  page?: number;
  status?: string;
  userId?: string;
  vendorProductId?: string;
}

@Injectable({
  providedIn: 'root'
})

export class PayrollService {

  constructor(
      private restangular: Restangular,
  ) {
  }

  private getProviderQuery(providerSlug?: string): Record<string, string> | undefined {
    const normalizedProviderSlug = `${providerSlug || ''}`.trim();
    return normalizedProviderSlug ? { providerSlug: normalizedProviderSlug } : undefined;
  }

  private companyProvider(companyId: string): any {
    return this.restangular
      .one('payroll/company')
      .one(companyId)
      .one('provider');
  }

  getWorkforceTeamCriteria(companyId: string, teamId: string): Promise<import('../payroll-workforce/workforce-team-criteria.state').SavedWorkforceCriteria> {
    return this.workforceTeamCreations(companyId).one('teams').one(teamId).one('criteria').get().toPromise();
  }
  saveWorkforceTeamCriteria(companyId: string, teamId: string, body: any): Promise<import('../payroll-workforce/workforce-team-criteria.state').SavedWorkforceCriteria> {
    return this.workforceTeamCreations(companyId).one('teams').one(teamId).one('criteria').customPUT(body).toPromise();
  }
  previewWorkforceCriteria(companyId: string, body: any): Promise<import('../payroll-workforce/workforce-team-criteria.state').CriteriaPreview> {
    return this.workforceTeamCreations(companyId).one('preview').customPOST(body).toPromise();
  }
  getWorkforceCriteriaOptions(companyId: string, body: any): Promise<{values: string[]; next: string | null}> {
    return this.workforceTeamCreations(companyId).one('criteria-options').customPOST(body).toPromise();
  }
  private workforceTeamCreations(companyId: string): any {
    return this.restangular.one('payroll/company').one(companyId).one('workforce').one('team-creations');
  }
  submitWorkforceTeam(companyId: string, request: TeamCreationIntent, key: string): Promise<TeamCreationProgress> {
    return this.workforceTeamCreations(companyId).customPOST(request, undefined, undefined, {'Idempotency-Key': key}).toPromise();
  }
  getWorkforceTeamCreation(companyId: string, operationId: string): Promise<TeamCreationProgress> {
    return this.workforceTeamCreations(companyId).one(operationId).get().toPromise();
  }
  recoverWorkforceTeamCreations(companyId: string): Promise<{operations: TeamCreationProgress[]}> {
    return this.workforceTeamCreations(companyId).get().toPromise();
  }
  continueWorkforceTeamCreation(companyId: string, operationId: string): Promise<TeamCreationProgress> {
    return this.workforceTeamCreations(companyId).one(operationId).one('continue').customPOST({}).toPromise();
  }
  closeWorkforceTeamCreation(companyId: string, operationId: string): Promise<TeamCreationProgress> {
    return this.workforceTeamCreations(companyId).one(operationId).one('close').customPOST({}).toPromise();
  }

  getWorkforceBrowseContext(companyId: string): Promise<WorkforceContext> {
    return this.restangular.one('payroll/company').one(companyId).one('workforce').one('browse-context').get().toPromise();
  }

  getWorkforceEmployees(companyId: string, query: Record<string, string>): Promise<WorkforcePage> {
    return this.restangular.one('payroll/company').one(companyId).one('workforce').one('employees').get(query).toPromise();
  }

  getWorkforceFilterOptions(companyId: string, query: Record<string, string>): Promise<WorkforceOptions> {
    return this.restangular.one('payroll/company').one(companyId).one('workforce').one('filter-options').get(query).toPromise();
  }

  getPayrollProviderCatalog(): Promise<PayrollProviderCatalogItem[]> {
    return this.restangular.one('payroll/providers/catalog').getList().toPromise();
  }

  getPayrollProviderConnectionStatusSummary(): Promise<PayrollProviderConnectionStatusSummary> {
    return this.restangular.one('payroll/providers/connection-summary').get().toPromise();
  }

  getCompanyPayrollProviderConnection(
    companyId: string,
    providerSlug?: string,
  ): Promise<PayrollProviderConnectionResponse | null> {
    return this.restangular
      .one('payroll/company')
      .one(companyId)
      .one('providerConnection')
      .get(this.getProviderQuery(providerSlug))
      .toPromise();
  }

  upsertUkgReadyConnectionSetup(
    companyId: string,
    body: UkgReadyConnectionSetupInput,
  ): Promise<UkgReadyConnectionSetupResponse> {
    return this.restangular
      .one('payroll/company')
      .one(companyId)
      .one('providerConnection')
      .one('ukg-ready')
      .customPUT(body)
      .toPromise();
  }

  upsertPaycorConnectionSetup(
    companyId: string,
    body: PaycorConnectionSetupInput,
  ): Promise<PaycorConnectionSetupResponse> {
    return this.restangular
      .one('payroll/company')
      .one(companyId)
      .one('providerConnection')
      .one('paycor')
      .customPUT(body)
      .toPromise();
  }

  discoverPaycorLegalEntities(
    companyId: string,
    body: PaycorConnectionSetupInput,
  ): Promise<PaycorLegalEntityDiscoveryResponse> {
    return this.restangular
      .one('payroll/company')
      .one(companyId)
      .one('providerConnection')
      .one('paycor')
      .one('legal-entities')
      .customPOST(body)
      .toPromise();
  }

  deactivateCompanyPayrollProviderConnection(
    companyId: string,
    providerSlug?: string,
  ): Promise<any> {
    return this.restangular
      .one('payroll/company')
      .one(companyId)
      .one('providerConnection')
      .remove(this.getProviderQuery(providerSlug))
      .toPromise();
  }

  refreshCompanyPayrollProviderConnectionHealth(
    companyId: string,
    providerSlug?: string,
  ): Promise<PayrollProviderConnectionHealthCheck> {
    return this.restangular
      .one('payroll/company')
      .one(companyId)
      .one('providerConnection')
      .one('health')
      .customPOST({}, undefined, this.getProviderQuery(providerSlug))
      .toPromise();
  }

  providerSupportsCapability(
    connection: PayrollProviderConnectionResponse | null | undefined,
    capability: keyof PayrollProviderCapabilities,
  ): boolean {
    const capabilities = connection?.capabilities || connection?.provider?.capabilities || {};
    return capabilities[capability] === true;
  }

  hasActivePayrollProviderConnection(
    connection: PayrollProviderConnectionResponse | null | undefined,
  ): boolean {
    const connectionStatus = `${connection?.connection?.status || ''}`.trim().toLowerCase();
    return !!connection?.connection?.id
      && connection.connection.isActive !== false
      && connectionStatus !== 'inactive'
      && connectionStatus !== 'disconnected'
      && connectionStatus !== 'unavailable';
  }

  providerConnectionSupportsCapability(
    connection: PayrollProviderConnectionResponse | null | undefined,
    capability: keyof PayrollProviderCapabilities,
  ): boolean {
    return this.hasActivePayrollProviderConnection(connection)
      && this.providerSupportsCapability(connection, capability);
  }

  providerCapabilityUnsupportedReason(
    connection: PayrollProviderConnectionResponse | null | undefined,
    capability: keyof PayrollProviderCapabilities,
    operationLabel: string,
  ): string {
    if (!connection) {
      return '';
    }

    const capabilities = connection.capabilities || connection.provider?.capabilities || {};
    if (capabilities[capability] !== false) {
      return '';
    }

    const providerName = connection.provider?.name || connection.connection?.displayName || 'This payroll provider';
    return `${providerName} does not support ${operationLabel}.`;
  }

  getCensusForPayrollProviderCompany(
    companyId: string,
    providerSlug?: string,
    options: PayrollCensusRequestOptions = {},
  ): Promise<PayrollCensusResponse> {
    return this.companyProvider(companyId)
      .one('census')
      .get({
        ...(this.getProviderQuery(providerSlug) || {}),
        includeCompleteness: 'true',
        ...(options.includeFilterOptions ? { includeFilterOptions: 'true' } : {}),
        ...(options.refresh ? { refresh: 'true' } : {}),
      })
      .toPromise();
  }

  getCensusFilterOptionsForPayrollProviderCompany(companyId: string, providerSlug?: string): Promise<any> {
    return this.companyProvider(companyId)
      .one('census')
      .one('filter-options')
      .get(this.getProviderQuery(providerSlug))
      .toPromise();
  }

  getCensusMetadataForPayrollProviderCompany(companyId: string, providerSlug?: string): Promise<any> {
    return this.companyProvider(companyId)
      .one('census')
      .one('metadata')
      .get(this.getProviderQuery(providerSlug))
      .toPromise();
  }

  getDeductionCodesForPayrollProviderCompany(
    companyId: string,
    providerSlug?: string,
  ): Promise<UkgDeductionCode[]> {
    return this.companyProvider(companyId)
      .one('deduction-codes')
      .get(this.getProviderQuery(providerSlug))
      .toPromise();
  }

  getFilteredCensusForPayrollProviderCompany(
    companyId: string,
    body: any,
    providerSlug?: string,
  ): Promise<any> {
    return this.companyProvider(companyId)
      .one('census')
      .one('filter')
      .customPOST(body, undefined, this.getProviderQuery(providerSlug))
      .toPromise();
  }

  getUkgCompanies(): Promise<any[]> {
    return this.restangular.one('payroll/ukg/allCompanies').getList().toPromise();
  }

  getUkgStatusSummary(): Promise<UkgStatusSummary> {
    return this.restangular.one('payroll/ukg/status-summary').get().toPromise();
  }

  refreshUkgStatusSummary(scope: 'impacted' | 'all' = 'impacted'): Promise<UkgStatusRefreshResult> {
    return this.restangular.one('payroll/ukg/status-summary').one('refresh').customPOST({ scope }).toPromise();
  }

  resetUkgDataForAllCompanies(body: any): Promise<any> {
    return this.restangular.one('payroll/ukg/reset/all').customPOST(body).toPromise();
  }

  resetUkgDataForCompany(companyId: string, body: any): Promise<any> {
    return this.restangular
      .one('payroll/ukg/company')
      .one(companyId)
      .one('reset')
      .customPOST(body)
      .toPromise();
  }

  getCompanyPayrollDeductionSetup(
    companyId: string,
    providerSlug?: string,
    options: PayrollDeductionSetupQueryOptions = {},
  ): Promise<PayrollDeductionSetup> {
    const providerQuery = this.getProviderQuery(providerSlug);
    const query = (providerQuery || options.includeLiveDeductionCodes === false)
      ? {
          ...(providerQuery || {}),
          ...(options.includeLiveDeductionCodes === false ? { includeLiveDeductionCodes: 'false' } : {}),
        }
      : undefined;

    return this.restangular
      .one('payroll/company')
      .one(companyId)
      .one('provider')
      .one('payroll-deduction-setup')
      .get(query)
      .toPromise();
  }

  updateCompanyPayrollDeductionSetup(
    companyId: string,
    body: {
      allowedDeductionCodes: Array<Partial<PayrollDeductionSetupCode>>;
      productPayrollDeductionMap: Record<string, PayrollDeductionProductMapping>;
    },
    providerSlug?: string,
  ): Promise<PayrollDeductionSetup> {
    return this.restangular
      .one('payroll/company')
      .one(companyId)
      .one('provider')
      .one('payroll-deduction-setup')
      .customPUT(body, undefined, this.getProviderQuery(providerSlug))
      .toPromise();
  }

  listCompanyPayrollDeductionImports(
    companyId: string,
    query: PayrollDeductionImportListQuery = {},
  ): Promise<PayrollDeductionImportListResponse> {
    return this.restangular
      .one('payroll/company')
      .one(companyId)
      .one('provider')
      .one('deduction-syncs')
      .customGET('', query)
      .toPromise();
  }

  getCompanyPayrollDeductionImport(companyId: string, importId: string): Promise<PayrollDeductionImportRecord> {
    return this.restangular
      .one('payroll/company')
      .one(companyId)
      .one('provider')
      .one('deduction-syncs')
      .one(importId)
      .get()
      .toPromise();
  }

  replayCompanyPayrollDeductionImport(
    companyId: string,
    importId: string,
    reason: string,
  ): Promise<PayrollDeductionImportRecord> {
    return this.restangular
      .one('payroll/company')
      .one(companyId)
      .one('provider')
      .one('deduction-syncs')
      .one(importId)
      .one('replay')
      .customPOST({ reason })
      .toPromise();
  }

  refreshCompanyPayrollDeductionImportStatuses(
    companyId: string,
    query: PayrollDeductionImportListQuery = {},
  ): Promise<PayrollDeductionImportRefreshResponse> {
    return this.restangular
      .one('payroll/company')
      .one(companyId)
      .one('provider')
      .one('deduction-syncs')
      .one('refresh-statuses')
      .customPOST(query)
      .toPromise();
  }

  refreshCompanyPayrollDeductionImportStatus(
    companyId: string,
    importId: string,
  ): Promise<PayrollDeductionImportRecord> {
    return this.restangular
      .one('payroll/company')
      .one(companyId)
      .one('provider')
      .one('deduction-syncs')
      .one(importId)
      .one('refresh-status')
      .customPOST({})
      .toPromise();
  }

  createTeamForCompanyFromEmployeeEmails(userBrand: string, companyId: string, body: any): Promise<any> {
    const brand = AppSettings.getBrandOverride() || userBrand;
    return this.companyProvider(companyId)
      .one('team')
      .one('from-employee-emails')
      .customPOST(body, undefined, brand ? { brand } : undefined)
      .toPromise();
  }

  createTeamFromFilteredCensusForCompany(userBrand: string, companyId: string, body: any): Promise<any> {
    const brand = AppSettings.getBrandOverride() || userBrand;
    return this.companyProvider(companyId)
      .one('team')
      .one('from-filtered-census')
      .customPOST(body, undefined, brand ? { brand } : undefined)
      .toPromise();
  }

  createTeamFromCensusAttributeSnapshotForCompany(userBrand: string, companyId: string, body: any): Promise<any> {
    const brand = AppSettings.getBrandOverride() || userBrand;
    return this.companyProvider(companyId)
      .one('team')
      .one('from-census-attribute-snapshot')
      .customPOST(body, undefined, brand ? { brand } : undefined)
      .toPromise();
  }

  addTeamMembersFromCensusEmployeeEmails(
    userBrand: string,
    companyId: string,
    teamId: string,
    body: any,
  ): Promise<any> {
    const brand = AppSettings.getBrandOverride() || userBrand;
    return this.companyProvider(companyId)
      .one('team')
      .one(teamId)
      .one('members')
      .one('from-employee-emails')
      .customPOST(body, undefined, brand ? { brand } : undefined)
      .toPromise();
  }

  refreshTeamFromCensusFiltersForCompany(
    userBrand: string,
    companyId: string,
    teamId: string,
  ): Promise<any> {
    const brand = AppSettings.getBrandOverride() || userBrand;
    return this.companyProvider(companyId)
      .one('team')
      .one(teamId)
      .one('refresh-from-census-filters')
      .customPOST({}, undefined, brand ? { brand } : undefined)
      .toPromise();
  }

  getWorkforceTeamSettings(companyId: string, teamId: string): Promise<SavedTeamSettings> {
    return this.restangular.one('payroll').one('company', companyId).one('workforce').one('teams', teamId)
      .one('settings').get().toPromise();
  }

  saveWorkforceTeamSettings(companyId: string, teamId: string, request: TeamSettingsRequest): Promise<SavedTeamSettings> {
    return this.restangular.one('payroll').one('company', companyId).one('workforce').one('teams', teamId)
      .one('settings').customPUT(request).toPromise();
  }

  updateTeamForCompany(userBrand: string, companyId: string, teamId: string, body: any): Promise<any> {
    const brand = AppSettings.getBrandOverride() || userBrand;
    return this.companyProvider(companyId)
      .one('team')
      .one(teamId)
      .customPUT(body, undefined, brand ? { brand } : undefined)
      .toPromise();
  }

  updateTeamAutoAddNewMembers(userBrand: string, companyId: string, teamId: string, autoAddNewMembers: boolean): Promise<any> {
    const brand = AppSettings.getBrandOverride() || userBrand;
    return this.companyProvider(companyId)
      .one('team')
      .one(teamId)
      .one('auto-add-new-members')
      .customPATCH({ autoAddNewMembers }, undefined, brand ? { brand } : undefined)
      .toPromise();
  }

  getCachedUkgConnectionHealth(company: any): UkgConnectionCachedHealth | null {
    const health = company?.payrollConfig?.payroll?.ukg?.health;
    if (!health || typeof health !== 'object' || Array.isArray(health)) {
      return null;
    }
    return health;
  }

  hasCachedUkgConnectionHealth(company: any): boolean {
    return !!this.getCachedUkgConnectionHealth(company);
  }

  getCachedUkgConnectionState(company: any): PayrollConnectionHealth {
    const health = this.getCachedUkgConnectionHealth(company);
    if (!health?.state) {
      return 'none';
    }
    return health.state === 'healthy' ? 'healthy' : 'error';
  }

  getUkgConnectedAt(company: any): string | null {
    const connectedAt = company?.payrollConfig?.payroll?.ukg?.connectedAt;
    return connectedAt ? `${connectedAt}` : null;
  }
}
